import { useBillingRates } from "@/hooks/use-billing-rates";
import { useTimebankBalance } from "@/hooks/use-timebank";
import { formatHours, parseISODate } from "@/lib/format";
import { MONTH_NAMES_NB } from "@/lib/completed-period-filter";
import type { TimebankTransaction } from "@/hooks/use-timebank";

/**
 * VIB-1286: kontekstlinje for godkjenner — kapasitet/fravær i
 * uttaksmåneden + attestert-status, så uttaket kan avstemmes mot
 * kapasitet og månedslukking uten å hoppe til en annen side.
 *
 * Gjenbruker useBillingRates(year) (samme queryKey på tvers av rader —
 * React Query deduper til ETT kall, ikke ett per rad).
 *
 * VIB-2766: saldoen kom i tillegg, og den er egentlig den viktigste av dem.
 * Uten den godkjente lederen et uttak uten å se hva søkeren hadde stående.
 * `useTimebankBalance` er nøklet på bruker-id, så flere rader fra samme
 * person deler ett kall på samme måte som kapasitetsdataen.
 */
export function TimebankRequestContextLine({ tx }: { tx: TimebankTransaction }) {
  const dateRef = tx.start_date ?? tx.end_date;
  // VIB-1339: dato-only-strenger parses tidssone-nøytralt (parseISODate),
  // ikke new Date(str) — unngår måned-/årsskifte i tidssoner vest for UTC.
  const parsed = dateRef ? parseISODate(dateRef) : new Date();
  const year = parsed.getFullYear();
  const month = parsed.getMonth() + 1;
  const { data } = useBillingRates(year);
  const { data: timebank } = useTimebankBalance(tx.user_id);

  const employee = dateRef ? data?.employees.find((e) => e.userId === tx.user_id) : undefined;
  const monthData = employee?.months.find((m) => m.month === month && m.year === year);

  // Saldoen vises uavhengig av kapasitetsdataen. Før VIB-2766 forsvant hele
  // linja når måneden manglet i billing-dataen, og for en oppsparing uten
  // dato ble den aldri tegnet i det hele tatt.
  if (!timebank && !monthData) return null;

  return (
    <p className="text-[11px] text-muted-foreground mt-0.5">
      {timebank && (
        <>
          Saldo: {formatHours(timebank.balance)}
          {timebank.pendingWithdrawal > 0 && (
            <span>
              {" "}
              (– {formatHours(timebank.pendingWithdrawal)} til behandling)
            </span>
          )}
          {monthData && " · "}
        </>
      )}
      {monthData && (
        <>
          kapasitet {MONTH_NAMES_NB[month - 1]}: {formatHours(monthData.capacity)} · fravær{" "}
          {formatHours(monthData.absenceHours)}
          {monthData.confirmed && <span className="text-amber-700"> · måned lukket</span>}
        </>
      )}
    </p>
  );
}
